// @ts-check
// =============================================================================
// js/app/audio.js — the SFX system (port of the old SFX map :119 + PlaySound
// :7071 + the log-substring cue mapping :6733).
//
// HOW IT WORKS
//   Twelve clips, self-hosted under /sfx/. playSound() clones the Audio node
//   so overlapping plays don't cut each other off, applies the user's volume
//   (settings.sfx, 0–100) and a little random pitch/volume variance so
//   repeated sounds don't feel robotic.
//
//   soundsForLogLine() reproduces the old client's trick of firing cues off
//   the duel log text — the server's log line IS the event stream.
// =============================================================================

import gsap from 'gsap';
import { store } from '../core/store.js';

/** Clip name -> Audio element (same v1/v2 choices the old client shipped). */
const SFX = {};

// -----------------------------------------------------------------------------
// Focus fade: the game NEVER makes noise when the browser is out of focus.
// New sounds are hard-skipped while unfocused/hidden, and the master volume
// TWEENS out when focus leaves (already-playing clips fade instead of
// cutting) and back in when it returns.
// -----------------------------------------------------------------------------

/** Master fade multiplier: 1 focused, 0 away. Tweened on focus changes. */
const focusFade = { volume: typeof document !== 'undefined' && document.hasFocus() ? 1 : 0 };

/** Clips currently playing — the fade re-applies to them as it moves. */
const activeClips = new Set();

/** Push the current fade level into every playing clip. */
function applyFocusFade() {
	for (const clip of activeClips) {
		clip.volume = Math.min(1, (clip.__baseVolume || 0) * focusFade.volume);
	}
}

/**
 * Ease the master fade toward a target (0.45s — long enough to feel like a
 * fade, short enough that returning players aren't in silence).
 * @param {number} target 0 or 1.
 */
function tweenFocusFade(target) {
	gsap.killTweensOf(focusFade);
	gsap.to(focusFade, { volume: target, duration: 0.45, ease: 'sine.out', onUpdate: applyFocusFade });
}

if (typeof window !== 'undefined') {
	window.addEventListener('blur', () => tweenFocusFade(0));
	window.addEventListener('focus', () => tweenFocusFade(1));
	document.addEventListener('visibilitychange', () => {
		if (document.hidden) {
			// A hidden tab stops ticking rAF — a tween would freeze mid-fade.
			gsap.killTweensOf(focusFade);
			focusFade.volume = 0;
			applyFocusFade();
		} else if (document.hasFocus()) {
			tweenFocusFade(1);
		}
	});
}

/** Lazily create the Audio elements (must happen after user interaction on
 *  some browsers anyway; the first call wires them). */
function ensureLoaded() {
	if (SFX.DrawCard) return;
	const files = {
		AltWinCon: 'DB_sfx_WinningMatchALT_v2.wav',
		AltLosing: 'DB_sfx_LosingMatchALT_v2.wav',
		CreatureAttacking: 'DB_sfx_CreatureAttacking_v2.wav',
		DevourCreature: 'DB_sfx_DevourCreature_v2.wav',
		DrawCard: 'DB_sfx_DrawCard_v1.wav',
		EndTurn: 'DB_sfx_EndTurn_v1.wav',
		GameStart: 'DB_sfx_GameStart_v2.wav',
		HarvestCrops: 'DB_sfx_HarvestCrops_v2.wav',
		LosingMatch: 'DB_sfx_LosingMatch_v1.wav',
		PlayCard: 'DB_sfx_PlayCard_v1.wav',
		PlayEvent: 'DB_sfx_PlayEvent_v1.wav',
		StartTurn: 'DB_sfx_StartTurn_v1.wav',
		WinningMatch: 'DB_sfx_WinningMatch_v1.wav',
	};
	for (const [name, file] of Object.entries(files)) {
		SFX[name] = new Audio('/sfx/' + file);
		SFX[name].load();
	}
}

/**
 * Play a named clip at the user's SFX volume with slight random variance.
 * Silent no-op when the user muted (settings.sfx == 0) or the clip is unknown.
 * @param {string} name  One of the SFX keys ("DrawCard", "PlayCard", …).
 * @returns {void}
 */
export function playSound(name) {
	const volumeSetting = store.userData?.settings?.sfx;
	if (!volumeSetting) return; // muted or not logged in
	// NEVER start a sound while the browser is out of focus — cues queued
	// during an absence (delayed log cues, background updates) stay silent.
	if (document.hidden || !document.hasFocus()) return;
	ensureLoaded();
	const sfx = SFX[name];
	if (!sfx) return;

	const clone = /** @type {HTMLAudioElement} */ (sfx.cloneNode());
	clone.__baseVolume = Math.min(1, (0.9 + Math.random() * 0.1) * (volumeSetting / 100));
	clone.volume = Math.min(1, clone.__baseVolume * focusFade.volume);
	clone.playbackRate = 0.8 + Math.random() * 0.4;
	clone.currentTime = 0;
	// Track while playing so the focus fade can reach it mid-clip.
	activeClips.add(clone);
	const untrack = () => activeClips.delete(clone);
	clone.addEventListener('ended', untrack);
	clone.addEventListener('error', untrack);
	clone.play().catch(() => {
		// Autoplay policy can reject before the first user gesture — fine.
		untrack();
	});
}

/**
 * Which cues does a new duel-log line trigger? (Old Duel Update handler
 * :6735–6767 — the substring matches are the contract with the server's log
 * strings; do not "clean them up".)
 * @param {string} logLine       The incoming duel.log HTML.
 * @param {any} newDuel          The duel state that came with it.
 * @returns {Array<{name: string, delayMs: number}>}
 */
export function soundsForLogLine(logLine, newDuel) {
	/** @type {Array<{name: string, delayMs: number}>} */
	const cues = [];
	if (!logLine) return cues;
	if (logLine.includes('Draw')) cues.push({ name: 'DrawCard', delayMs: 0 });
	if (logLine.includes('Play')) cues.push({ name: 'PlayCard', delayMs: 0 });
	if (logLine.includes('Harvest')) cues.push({ name: 'HarvestCrops', delayMs: 0 });
	if (logLine.includes('attacks')) cues.push({ name: 'CreatureAttacking', delayMs: 0 });
	if (logLine.includes('Devoured')) cues.push({ name: 'DevourCreature', delayMs: 0 });
	if (logLine.includes('turn!')) {
		cues.push({ name: newDuel.player1.tick ? 'StartTurn' : 'EndTurn', delayMs: 1000 });
	}
	if (logLine.includes('GAME OVER')) {
		cues.push({ name: newDuel.player2.deck === 0 ? 'WinningMatch' : 'LosingMatch', delayMs: 1000 });
	}
	return cues;
}
